import { useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { OrdersTab } from "@/components/admin/OrdersTab";
import { LeadsTab } from "@/components/admin/LeadsTab";
import { ReviewsTab } from "@/components/admin/ReviewsTab";
import { PromosTab } from "@/components/admin/PromosTab";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { EmailsTab } from "@/components/admin/EmailsTab";
import { ExportTab } from "@/components/admin/ExportTab";
import { ChangePasswordDialog } from "@/components/admin/ChangePasswordDialog";
import { adminApi, getToken, setToken } from "@/lib/api";

export default function Admin() {
  const [user, setUser] = useState(getToken() ? null : false);

  useEffect(() => {
    document.title = "ClimNova – Admin";
    const id = adminApi.interceptors.response.use((r) => r, (err) => {
      if (err?.response?.status === 401 && getToken()) { setToken(null); setUser(false); }
      return Promise.reject(err);
    });
    if (getToken()) adminApi.get("/me").then(({ data }) => setUser(data.email)).catch(() => { setToken(null); setUser(false); });
    return () => adminApi.interceptors.response.eject(id);
  }, []);

  if (user === null) return <div className="min-h-screen bg-ice-soft" />;
  if (user === false) return <AdminLogin onLogged={setUser} />;

  const logout = () => { setToken(null); setUser(false); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ice-soft">
      <header className="bg-white border-b border-slate-200">
        <div className="container-x h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span data-testid="admin-header-title" className="font-display text-xl font-extrabold text-slate-900">ClimNova – Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:inline">{user}</span>
            <ChangePasswordDialog />
            <button type="button" onClick={logout} data-testid="admin-logout-button" className="btn-ghost-ice !py-2">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>
      <main className="container-x py-8">
        <Tabs defaultValue="orders">
          <TabsList className="mb-6 bg-white border border-slate-200">
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Commandes</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Demandes de rappel</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Avis</TabsTrigger>
            <TabsTrigger value="promos" data-testid="admin-tab-promos">Codes promo</TabsTrigger>
            <TabsTrigger value="emails" data-testid="admin-tab-emails">E-mails</TabsTrigger>
            <TabsTrigger value="export" data-testid="admin-tab-export">Export comptable</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Réglages</TabsTrigger>
          </TabsList>
          <TabsContent value="export"><ExportTab /></TabsContent>
          <TabsContent value="orders"><OrdersTab /></TabsContent>
          <TabsContent value="leads"><LeadsTab /></TabsContent>
          <TabsContent value="reviews"><ReviewsTab /></TabsContent>
          <TabsContent value="promos"><PromosTab /></TabsContent>
          <TabsContent value="emails"><EmailsTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
